import { Injectable, inject } from '@angular/core';
import { InAppBrowser } from '@awesome-cordova-plugins/in-app-browser/ngx';
import { Platform } from '@ionic/angular/standalone';
import { differenceInMinutes, format, parseISO } from 'date-fns';
import { arSA, enUS } from 'date-fns/locale';
import { BehaviorSubject, Observable, from, throwError } from 'rxjs';
import { catchError, first, map, tap } from 'rxjs/operators';
import {
  HOURLY_SERVICES,
  HourlyRequestState,
  HourlyServiceIds,
  IAddUserEvaluationDto,
  IAvailableDateOptionsDto,
  IAvailableVisitDate,
  ICountryDto,
  IDayOffAvailableDateOptionsDto,
  IHospitalityUniformDto,
  IHourlyCountriesFiltersDto,
  IHourlyDatePackageOptionsDto,
  IHourlyDurationFiltersDto,
  IHourlyGeneralSubmitDto,
  IHourlyMonthDto,
  IHourlyNumberOfHoursFiltersDto,
  IHourlyNumberOfMonthsFiltersDto,
  IHourlyNumberOfResourcesFiltersDto,
  IHourlyPackageDto,
  IHourlyPackageFiltersDto,
  IHourlyRequestDetailsDto,
  IHourlyRequestDto,
  IHourlyRequestsSummaryDto,
  IHourlyServiceDto,
  IHourlyServiceTypeDetails,
  IHourlyServiceTypeDto,
  IHourlySubmitDto,
  IHourlySummaryDto,
  IHourlySummaryOptionsDto,
  IHourlyWeekdayFiltersDto,
  IPaymentDetailsDto,
  IPaymentOptionsDto,
  IPricedAvailableDateDto,
  IPricedAvailableDateOptionsDto,
  IRecurringAvailableDateOptionsDto,
  ISpaPackageDto,
  ISpaPackageFiltersDto,
  IVisitResource,
  IVisitTimeCountdownTimerDto,
  IWorktimeDto,
  IWorktimeFiltersDto,
  TranslationKeys,
  VisitAction,
  WeekdayId,
} from 'src/app/contracts';
import { Api } from 'src/app/core/common/api';
import { Auth } from 'src/app/core/common/auth';
import { APP_DATE_FORMAT, IApiOptions, Language, settings$ } from 'src/app/core/contracts';
import { setStorageItem } from 'src/app/core/helpers/storage';
import { UI } from 'src/app/core/helpers/ui';
import { getPlatform, isArabic, toHttpParams } from 'src/app/core/helpers/utils';
import { Cacheable } from 'ts-cacheable';

export const mapToWeekdayId = (date: Date) => (date.getDay() + 1) as WeekdayId; // map 0-6 to 1-7

@Injectable({ providedIn: 'root' })
export class HourlyData {
  private api = inject(Api);
  private ui = inject(UI);
  private platform = inject(Platform);
  private iab = inject(InAppBrowser);
  private auth = inject(Auth);

  language = Language.en;
  public services = new BehaviorSubject<IHourlyServiceDto[]>([]);

  constructor() {
    settings$.subscribe(settings => (this.language = settings.language));
  }
  public get services$() {
    return this.services.asObservable();
  }
  public getPaymentDetails(id: number, options?: IPaymentOptionsDto, apiOptions?: IApiOptions): Observable<IPaymentDetailsDto> {
    return this.api.get(`hourly/${id}/payment-details`, { ...apiOptions, params: toHttpParams(options) });
  }
  public getDelayRequestPaymentDetails(id: number, options: IPaymentOptionsDto = null): Observable<IPaymentDetailsDto> {
    const params = toHttpParams(options);
    return this.api.get(`hourly/visits/delay-request-payment-details/${id}`, { params });
  }
  public getServices(forceUpdate?: boolean, options?: IApiOptions): Promise<IHourlyServiceDto[]> {
    return new Promise(resolve => {
      if (forceUpdate || !this.services.getValue()?.length) {
        this.api
          .get<IHourlyServiceDto[]>('hourly/services', options)
          .pipe(
            catchError(err => {
              // fallback to current value
              // not calling reject to avoid requiring a 'catch' on callers, especially in loader.ts
              resolve(this.services.getValue());
              return throwError(() => err as unknown);
            })
          )
          .subscribe((services: IHourlyServiceDto[]) => {
            setStorageItem(HOURLY_SERVICES, services);
            this.services.next(services);
            resolve(services);
          });
      } else {
        resolve(this.services.getValue());
      }
    });
  }
  getHomeServices(): Observable<IHourlyServiceDto[]> {
    return this.api.get('hourly/home-services');
  }
  getService(id: HourlyServiceIds, forceUpdate?: boolean, options?: IApiOptions) {
    if (forceUpdate || !this.services.getValue()?.length) {
      return from(this.getServices(true, options)).pipe(map(i => i.find(k => k.id === (id as number))));
    }
    return this.services$.pipe(
      first(),
      map(i => i.find(k => k.id === (id as number)))
    );
  }
  getHospitalityServiceTypes(): Observable<IHourlyServiceTypeDto[]> {
    return this.api.get(`hourly/hospitality/types`);
  }
  public getCountries(filters: IHourlyCountriesFiltersDto, options?: IApiOptions): Observable<ICountryDto[]> {
    options = options || {};
    options.params = toHttpParams(filters);
    return this.api.get<ICountryDto[]>('hourly/countries', options);
  }
  public getDurations(filters: IHourlyDurationFiltersDto, options?: IApiOptions): Observable<number[]> {
    return this.api.get<number[]>('hourly/durations', { ...options, params: toHttpParams(filters) });
  }
  public getNumberOfHours(filters: IHourlyNumberOfHoursFiltersDto, options?: IApiOptions): Observable<number[]> {
    options = options || {};
    options.params = toHttpParams(filters);
    return this.api.get<number[]>('hourly/number-of-hours', options);
  }
  public getNumberOfResources(filters: IHourlyNumberOfResourcesFiltersDto, options?: IApiOptions): Observable<number[]> {
    options = options || {};
    options.params = toHttpParams(filters);
    return this.api.get<number[]>('hourly/number-of-resources', options);
  }
  public getNumberOfMonths(filters: IHourlyNumberOfMonthsFiltersDto, options?: IApiOptions): Observable<IHourlyMonthDto[]> {
    options = options || {};
    options.params = toHttpParams(filters);
    return this.api.get<IHourlyMonthDto[]>('hourly/number-of-months', options);
  }
  public getWeekdayIds(filters: IHourlyWeekdayFiltersDto, options?: IApiOptions): Observable<WeekdayId[]> {
    options = options || {};
    options.params = toHttpParams(filters);
    return this.api.get<WeekdayId[]>('hourly/weekdays', options);
  }
  public getWorktimes(filters: IWorktimeFiltersDto, options?: IApiOptions): Observable<IWorktimeDto[]> {
    const locale = isArabic(this.language) ? arSA : enUS;
    return this.api.get<IWorktimeDto[]>('hourly/worktimes', { ...options, params: toHttpParams(filters) }).pipe(
      tap(worktimes => {
        for (const worktime of worktimes) {
          worktime.start = parseISO(worktime.start as string);
          worktime.end = parseISO(worktime.end as string);
          worktime.formattedStart = format(worktime.start, 'hh:mm a', { locale });
          worktime.formattedEnd = format(worktime.end, 'hh:mm a', { locale });
        }
      })
    );
  }
  public getPricedAvailableDates(options: IPricedAvailableDateOptionsDto) {
    return this.api.get<IPricedAvailableDateDto[]>('hourly/priced-available-dates', { params: toHttpParams(options) }).pipe(
      tap(availableDates => {
        for (const date of availableDates) {
          date.date = parseISO(date.date as string);
        }
      })
    );
  }
  public getRecurringAvailableDates(options: IRecurringAvailableDateOptionsDto) {
    return this.api.get<IPricedAvailableDateDto[]>('hourly/recurring-available-dates', { params: toHttpParams(options) }).pipe(
      tap(availableDates => {
        for (const date of availableDates) {
          date.date = parseISO(date.date as string);
        }
      })
    );
  }
  public getDayOffAvailableDates(options: IDayOffAvailableDateOptionsDto) {
    return this.api.get<IPricedAvailableDateDto[]>('hourly/day-off-available-dates', { params: toHttpParams(options) }).pipe(
      tap(availableDates => {
        for (const date of availableDates) {
          date.date = parseISO(date.date as string);
        }
      })
    );
  }
  public getSummary(options: IHourlySummaryOptionsDto) {
    const locale = isArabic(this.language) ? arSA : enUS;
    return this.api.get<IHourlySummaryDto>(`hourly/summary`, { params: toHttpParams(options) }).pipe(
      tap(summary => {
        if (summary?.calendar?.startDate) {
          summary.calendar.startDate = parseISO(summary.calendar.startDate as string);
          summary.calendar.formattedStartDate = format(summary.calendar.startDate, APP_DATE_FORMAT, { locale });
        }
        if (summary?.calendar?.visitDates) {
          summary.calendar.visitDates = summary.calendar.visitDates.map(date => parseISO(date as string));
        }
      })
    );
  }
  public getSpaPackages(filters: ISpaPackageFiltersDto, options?: IApiOptions): Observable<ISpaPackageDto[]> {
    return this.api.get<ISpaPackageDto[]>('hourly/spa/packages', { ...options, params: toHttpParams(filters) });
  }
  public getPackageCode(packageId: number, lineId: number, worktimeId: number, countryCode: string): string {
    return `${packageId}-${lineId}-${worktimeId}-${countryCode}`;
  }
  public breakPackageCode(packageCode: string): { packageId: number; lineId: number; worktimeId: number; countryCode: string } {
    const [packageId, lineId, worktimeId, countryCode] = packageCode.split('-');
    return {
      packageId: +packageId,
      lineId: +lineId,
      worktimeId: +worktimeId,
      countryCode,
    };
  }
  public submit(
    addressId: number,
    dates: IHourlyDatePackageOptionsDto[],
    includeCleaningTools?: boolean,
    extraServiceDetails?: IHourlyServiceTypeDetails,
    notes?: string
  ): Observable<number> {
    const data: IHourlyGeneralSubmitDto = {
      addressId,
      dates,
      cleaningTools: includeCleaningTools ? 1 : 0, // the app currently doesn't allow selecting more than 1 cleaning tool

      notes,
      extraServiceDetailsJSON: extraServiceDetails ? JSON.stringify(extraServiceDetails) : null,

      platformId: getPlatform(),
      userAgent: navigator.userAgent,
    };
    return this.api.post('hourly/submit', data);
  }
  public getRequestDetails(id: number, options?: IApiOptions): Observable<IHourlyRequestDetailsDto> {
    const locale = isArabic(this.language) ? arSA : enUS;
    return this.api.get<IHourlyRequestDetailsDto>(`hourly/${id}/details`, options).pipe(
      tap(details => {
        details.startDate = parseISO(details.startDate as string);
        details.formattedStartDate = format(details.startDate, APP_DATE_FORMAT, { locale });
        details.endDate = parseISO(details.endDate as string);
        for (const visit of details.visits) {
          visit.date = parseISO(visit.date as string);
          visit.formattedStartTime = this.ui.formatDate(visit.date);
          visit.day = format(visit.date, 'dd');
          visit.month = format(visit.date, isArabic ? 'MMMM' : 'MMM', { locale });
          visit.weekdayName = visit.weekdayName?.replace(/^ال/, '');
          visit.startTime = parseISO(visit.startTime as string);
          visit.endTime = parseISO(visit.endTime as string);
          visit.durationInMinutes = differenceInMinutes(visit.endTime, visit.startTime);
        }
      })
    );
  }
  @Cacheable({ maxAge: 10_000 }) // added to avoid extra call caused by getRouteTitle (to retrieve service name) in hourly-routing.module.ts
  public getRequest(id: number, options?: IApiOptions): Observable<IHourlyRequestDto> {
    return this.api.get(`hourly/${id}`, options);
  }
  changeVisitDate(visitId: number, newDate: Date, paid?: boolean): Observable<number> {
    return this.api.post(`hourly/change-visit-date`, { visitId, newDate, paid });
  }
  getVisitEvaluationUrl(visitId: number): Observable<string> {
    return this.api.get(`hourly/visit-evaluation-url/${visitId}`);
  }
  getHourlyRequestEvaluationUrl(id: number): Observable<string> {
    return this.api.get(`hourly/${id}/evaluation-url`);
  }
  addUserEvaluation(userEvaluation: IAddUserEvaluationDto) {
    return this.api.post(`hourly/add-user-evaluation`, userEvaluation);
  }
  public actionsVisit(
    action: VisitAction,
    bookingId: number,
    visitId: number,
    delayedVisitDate: Date,
    employeeId: string = null,
    reason: string = null
  ): Observable<number> {
    return this.api.post('hourly/actions', { action, bookingId, visitId, delayedVisitDate, employeeId, reason });
  }
  public getVisitAvailableDates(visitId: number, month: Date = null): Observable<Date[]> {
    const options: IAvailableVisitDate = {
      visitId,
      month,
    };
    return this.api
      .get<string[]>('hourly/visit-available-dates', {
        params: toHttpParams(options),
      })
      .pipe(map((dates: string[]) => dates.map(i => parseISO(i))));
  }
  public getVisitResources(visitId: number): Observable<IVisitResource[]> {
    return this.api.get(`hourly/visit-resources/${visitId}`);
  }
  public viewContractAsPdf(requestId: number) {
    this.auth.user$.pipe(first()).subscribe(user => {
      const url = `${this.api.baseUrl}api/hourly/print/${requestId}/${user.sub}`;
      const browser = this.iab.create(url, '_system');
      browser.close();
    });
  }
  getActiveVisitCountdown(bookingId?: number) {
    const params = bookingId ? { bookingId } : null;
    return this.api.get<IVisitTimeCountdownTimerDto | null>('hourly/active-visit-countdown', { params, handleLoading: false }).pipe(
      tap(details => {
        if (details) {
          details.startTime = parseISO(details.startTime as string);
        }
      })
    );
  }

  //#region v4 - Obsolete - TODO:old-version-remove
  public get morningWorktimeId() {
    return 1;
  }
  public get eveningWorktimeId() {
    return 2;
  }
  private packages = new BehaviorSubject<IHourlyPackageDto[]>([]);
  private requestsSummary = new BehaviorSubject<IHourlyRequestsSummaryDto>(null);
  private worktimes = new BehaviorSubject<IWorktimeDto[]>([]);
  private requests = new BehaviorSubject<IHourlyRequestDto[]>([]);
  public get requestsSummary$() {
    return this.requestsSummary.asObservable();
  }
  public get packages$() {
    return this.packages.asObservable();
  }
  public get requests$() {
    return this.requests.asObservable();
  }
  public get worktimes$() {
    return this.worktimes.asObservable();
  }
  getCleaningServices(): Observable<IHourlyServiceDto[]> {
    return this.api.get('hourly/cleaning-services');
  }
  public getReceptionHospitalityUniforms(): Observable<IHospitalityUniformDto[]> {
    return this.api.get(`hourly/hospitality/reception-uniforms`);
  }
  public getCompanionServiceTypes(): Observable<IHourlyServiceTypeDto[]> {
    return this.api.get(`hourly/companion/types`);
  }
  resetPackages() {
    this.packages.next([]);
  }
  public async getWorktimes_Obsolete(serviceId: number, forceUpdate?: boolean, options?: IApiOptions): Promise<IWorktimeDto[]> {
    return new Promise(resolve => {
      if (forceUpdate || this.worktimes.getValue().length === 0) {
        options = options || {};
        options.handleLoading = false;
        this.api
          .get<IWorktimeDto[]>('hourly/worktimes/' + serviceId, options)
          .pipe(
            catchError(err => {
              this.worktimes.next([]); // clear previous list so as to not show worktimes of a different service
              resolve(this.worktimes.getValue());
              return throwError(() => err as unknown);
            })
          )
          .subscribe((worktimes: IWorktimeDto[]) => {
            this.worktimes.next(worktimes);
            resolve(worktimes);
          });
      } else {
        resolve(this.worktimes.getValue());
      }
    });
  }
  public async getPackages(filters: IHourlyPackageFiltersDto, forceUpdate?: boolean, options?: IApiOptions): Promise<IHourlyPackageDto[]> {
    return new Promise(resolve => {
      if (forceUpdate || this.packages.getValue().length === 0) {
        options = options || {};
        options.params = toHttpParams(filters);
        options.loadingMessage = TranslationKeys.DomainCommon.LoadingPackages;
        this.api
          .get<IHourlyPackageDto[]>('hourly/packages', options)
          .pipe(
            catchError(err => {
              resolve(this.packages.getValue());
              return throwError(() => err as unknown);
            })
          )
          .subscribe((packages: IHourlyPackageDto[]) => {
            this.packages.next(packages);
            resolve(packages);
          });
      } else {
        resolve(this.packages.getValue());
      }
    });
  }
  public getPackage(code: string, forceUpdate?: boolean): Observable<IHourlyPackageDto> {
    if (forceUpdate || this.packages.getValue().length === 0) {
      return this.api.get(this.api.baseUrl + 'api/hourly/package/' + code, {
        appendBaseUrl: false,
      });
    }
    return this.packages$.pipe(
      first(),
      map(packages => packages.find(i => i.code === code))
    );
  }
  public getAvailableDates(pkg: IHourlyPackageDto, latitude: number, longitude: number, month: Date = null): Observable<Date[]> {
    const options: IAvailableDateOptionsDto = {
      lineId: pkg.line.id,
      worktimeId: pkg.worktime.id,
      countryCode: pkg.country.code,
      latitude,
      longitude,
      month,
    };
    return this.api
      .get<string[]>(this.api.baseUrl + 'api/hourly/available-dates', {
        appendBaseUrl: false,
        params: toHttpParams(options),
      })
      .pipe(map((dates: string[]) => dates.map(i => parseISO(i))));
  }
  public getRequests(state?: HourlyRequestState, forceUpdate?: boolean): Promise<IHourlyRequestDto[]> {
    return new Promise<IHourlyRequestDto[]>(resolve => {
      if (forceUpdate || this.requests.getValue().length === 0) {
        this.api
          .get<IHourlyRequestDto[]>('hourly', {
            params: toHttpParams({ stateId: state }),
            handleLoading: false,
          })
          .pipe(
            catchError(err => {
              resolve(this.requests.getValue());
              return throwError(() => err as unknown);
            })
          )
          .subscribe(requests => {
            this.requests.next(requests);
            resolve(requests);
          });
      } else {
        resolve(this.requests.getValue());
      }
    });
  }
  public getRequestsSummary(forceUpdate?: boolean): Promise<IHourlyRequestsSummaryDto> {
    return new Promise(resolve => {
      if (forceUpdate || this.requestsSummary.getValue() === null) {
        this.api
          .get<IHourlyRequestsSummaryDto>('hourly/requests-summary', { handleLoading: false })
          .pipe(
            catchError(err => {
              resolve(this.requestsSummary.getValue());
              return throwError(() => err as unknown);
            })
          )
          .subscribe(summary => {
            this.requestsSummary.next(summary);
            resolve(summary);
          });
      } else {
        resolve(this.requestsSummary.getValue());
      }
    });
  }
  public getLatestRequest(options?: IApiOptions): Observable<IHourlyRequestDto> {
    options = options || {};
    return this.api.get('hourly/latest-request', options);
  }
  public saveHourlyNotes(requestId: number, notes: string) {
    return this.api.post('hourly/notes', { requestId, notes });
  }
  public submit_Obsolete(request: IHourlySubmitDto): Observable<number> {
    request.userAgent = navigator.userAgent; // currently just for logs
    return this.api.post(this.api.baseUrl + 'api/hourly/submit', request, {
      appendBaseUrl: false,
    });
  }
  //#endregion v4 - Obsolete
}
